/**
 * International Auctions — sale catalogue (F1.3, BXS-3627).
 *
 * Route: `/:locale/international-auctions/:house/:sale`
 *
 * The densest surface in the module: a co-branded sale header, a seven-field
 * filter rail, and the catalogue itself in either a table or a card grid.
 *
 * Four things here are load-bearing and must survive any refactor:
 *
 *  1. COLUMN HEADERS COME FROM THE HOUSE RECORD, NOT FROM i18n.
 *     `house.lot_label` is `Lot` at Tattersalls and Arqana but `Hip` at
 *     Fasig-Tipton; `house.consignor_label` is `Consignor` everywhere except
 *     Arqana, where it is `Vendor`. Those are DATA (models file, §6.6) — no
 *     amount of translation makes them a locale concern, so the template reads
 *     them straight off `sale.house` and never through a translation key.
 *
 *  2. THE OUTBOUND CTA GOES THROUGH THE LOGGED REDIRECT, NEVER STRAIGHT TO THE
 *     HOUSE. `buildGoToken('registration', house, sale)` builds the token and
 *     the server resolves `registration_url` from the sale row itself, writes an
 *     `external_events` row and 302s with UTM attached (§8.3). A registration
 *     click counts SEPARATELY from a lot click and is the stronger intent signal
 *     of the two (§8.4); linking direct would cost us every one of them.
 *
 *  3. EVERY FILTER LIVES IN THE URL. `?lot=&q=&sex=&year=&type=&consignor=`
 *     `&day=&page=&view=` — the same query-param pattern the buyer auctions
 *     list uses, so a filtered catalogue is shareable, back-button-able and
 *     server-renderable. The two free-text boxes debounce (400 ms) before they
 *     navigate; the selects navigate immediately.
 *
 *  4. A PRICE NEVER IMPLIES AN OUTCOME. Lot 4 of the July Mixed Sale is
 *     `not_sold` AND carries `4,500 gns` — the bid it failed to make. The
 *     status pill carries the outcome on its own and the price column just
 *     shows the money, so the two can co-exist without contradicting.
 *
 * Rendering rules the fixtures deliberately stress:
 *   - Lot 3 is WITHDRAWN: lot number only, every other field empty or null. It
 *     renders as a de-emphasised stub row that keeps every column in place — a
 *     muted dash per empty cell and the Withdrawn pill in the outcome column.
 *   - Lot 2 is UNNAMED: `display_title` already holds the house's own fallback
 *     (`Siyouni (FR) / Fabulous (IRE) B.F. (IRE)`). It is stored, so it is
 *     rendered verbatim and never re-derived here.
 *   - `formatLotPrice` returns `null` for "no price", which is a different
 *     thing from zero. A null price renders as a muted dash, never as `0`.
 *
 * SSR: no `window`, no `document`, no `localStorage`. `Intl` only, and the
 * debounce timers are created by user input, which never happens on the server.
 */
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Params, ParamMap, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject, Subscription, combineLatest, debounceTime } from 'rxjs';

import { environment } from 'src/environments/environment';
import { PaginationComponent } from 'src/app/Shared/components/pagination/pagination.component';
import { SkeletonLoaderComponent } from 'src/app/Shared/components/skeleton-loader/skeleton-loader.component';
import { LocaleLinkPipe } from 'src/app/Shared/_pipes/locale-link/locale-link.pipe';
import { LocaleService } from 'src/app/Shared/services/locale/locale.service';

import {
  DEFAULT_LOTS_PER_PAGE,
  InternationalAuctionsService,
  LotFilterOptions,
} from '../../../_services/international-auctions.service';
import {
  ExternalHouse,
  ExternalLot,
  ExternalLotStatus,
  ExternalSaleView,
  LotQuery,
  Paginated,
  SupportedLocale,
  buildGoToken,
  formatLotPrice,
  parseIsoDate,
  resolveTranslation,
} from '../_mock/international-auctions.models';
import { HouseBrand, resolveHouseBrand } from '../_shared/house-brand';
import { InternationalStatusChipComponent } from '../_shared/status-chip/international-status-chip.component';
import { InternationalFavouriteButtonComponent } from '../_shared/favourite-button/international-favourite-button.component';
import { InternationalMyListService } from '../../../_services/international-my-list.service';

/** One entry in the header's `dates · venue · lots` line. */
interface SaleHeaderFact {
  key: string;
  text: string;
  /** Tabular figures, so a date and a lot count do not wobble. */
  isNumeric: boolean;
}

/** `SupportedLocale` → the BCP-47 tag `Intl` should format with. */
const INTL_LOCALES: Record<SupportedLocale, string> = {
  en: 'en-GB',
  ar: 'ar-EG',
};

/**
 * Long enough that a typed word settles before we navigate, short enough that
 * the catalogue still feels live. Matches the debounce on the buyer list.
 */
const TEXT_FILTER_DEBOUNCE_MS = 400;

/** Spelled out rather than concatenated, so the keys survive an i18n sweep. */
const LOT_STATUS_KEYS: Record<ExternalLotStatus, string> = {
  catalogued: 'international_auctions.lot_status.catalogued',
  withdrawn: 'international_auctions.lot_status.withdrawn',
  sold: 'international_auctions.lot_status.sold',
  not_sold: 'international_auctions.lot_status.not_sold',
  private_sale: 'international_auctions.lot_status.private_sale',
};

const EMPTY_FILTER_OPTIONS: LotFilterOptions = {
  sexes: [],
  years: [],
  horseTypes: [],
  consignors: [],
  saleDays: [],
};

const FULL_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
};

/** Shared across SSR requests: both caches are pure functions of their key. */
const DATE_FORMATTERS = new Map<string, Intl.DateTimeFormat>();
const NUMBER_FORMATTERS = new Map<string, Intl.NumberFormat>();

function dateFormatter(locale: string): Intl.DateTimeFormat {
  let formatter = DATE_FORMATTERS.get(locale);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, FULL_DATE_OPTIONS);
    DATE_FORMATTERS.set(locale, formatter);
  }
  return formatter;
}

function numberFormatter(locale: string): Intl.NumberFormat {
  let formatter = NUMBER_FORMATTERS.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale);
    NUMBER_FORMATTERS.set(locale, formatter);
  }
  return formatter;
}

/**
 * One catalogue row, fully resolved.
 *
 * Built once per response rather than read through getters in the template:
 * `formatLotPrice` and the router command array would otherwise run 24 × 6
 * times per change-detection pass, and a fresh array literal per pass re-runs
 * `RouterLink.ngOnChanges` on every tick.
 */
export interface SaleCatalogueRow {
  id: number;
  lotNumber: string;
  /** `display_title` verbatim — stored by the house, never re-derived. */
  title: string;
  /** `Sire ✕ Dam`, or empty where the house published neither. */
  pedigree: string;
  consignor: string;
  horseType: string;
  /** `formatLotPrice` output. `null` means "no price", never zero. */
  price: string | null;
  status: ExternalLotStatus;
  statusKey: string;
  /** `catalogued` is the default state and earns no pill. */
  hasOutcome: boolean;
  /** Drives the de-emphasised stub row. */
  isWithdrawn: boolean;
  commands: unknown[];
}

@Component({
  selector: 'app-international-auctions-sale',
  standalone: true,
  imports: [
    RouterLink,
    TranslatePipe,
    LocaleLinkPipe,
    PaginationComponent,
    SkeletonLoaderComponent,
    InternationalStatusChipComponent,
    InternationalFavouriteButtonComponent,
  ],
  templateUrl: './international-auctions-sale.component.html',
  styleUrl: './international-auctions-sale.component.scss',
})
export class InternationalAuctionsSaleComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly myList = inject(InternationalMyListService);
  private readonly router = inject(Router);
  private readonly auctions = inject(InternationalAuctionsService);
  private readonly localeService = inject(LocaleService);
  private readonly cdr = inject(ChangeDetectorRef);

  /* --- Route ------------------------------------------------------------- */

  houseSlug = '';
  saleSlug = '';

  /* --- Data -------------------------------------------------------------- */

  /** True until `getSale` answers. Guards the not-found flash. */
  saleLoading = true;
  /** True while a catalogue page is in flight. Guards the empty-state flash. */
  lotsLoading = true;

  sale: ExternalSaleView | null = null;
  lots: Paginated<ExternalLot> | null = null;
  rows: SaleCatalogueRow[] = [];
  filterOptions: LotFilterOptions = EMPTY_FILTER_OPTIONS;

  locale: SupportedLocale = 'en';

  /* --- View state -------------------------------------------------------- */

  /**
   * `table` by default: the per-house column labels are the whole point of this
   * screen, and the URL without `?view=` is the one that gets shared.
   */
  catalogueView: 'table' | 'cards' = 'table';
  filtersOpen = true;

  /* --- Filter state (mirrors the query string) --------------------------- */

  lotNumberText = '';
  searchText = '';
  sex = '';
  yearFoaled = '';
  horseType = '';
  consignor = '';
  saleDay = '';
  page = 1;

  /** Placeholder rows while a page loads. Never rendered as real data. */
  readonly skeletonCards = [1, 2, 3, 4, 5, 6, 7, 8];

  private readonly subscriptions = new Subscription();
  /** One subject per text box, so a fast switch between them loses neither. */
  private readonly lotNumberInput$ = new Subject<string>();
  private readonly searchInput$ = new Subject<string>();

  /** Drops out-of-order responses — the mock answers on a 250 ms delay. */
  private saleRequestId = 0;
  private lotsRequestId = 0;

  /**
   * The value we last pushed into the URL ourselves. Lets the query-param
   * subscription tell "the user is mid-type" from "the URL changed underneath
   * us" (back button, Clear filters) without stealing the caret.
   */
  private committedLotNumber = '';
  private committedSearch = '';

  private dateRangeKey: string | null = null;
  private dateRangeValue = '';

  /** Keyed on the house object itself — see the `brand` getter. */
  private brandKey: ExternalHouse | null = null;
  private brandValue: HouseBrand = resolveHouseBrand(null);
  private houseCommandsKey: string | null = null;
  private houseCommandsValue: unknown[] = ['/international-auctions'];
  private registerHrefKey: string | null = null;
  private registerHrefValue = environment.outboundUrl;

  /* ---------------------------------------------------------------- *
   * Lifecycle
   * ---------------------------------------------------------------- */

  ngOnInit(): void {
    // Subscribed rather than read once: resolved translations, `Intl` dates and
    // the guinea/currency formatting all have to follow a language switch that
    // happens without a page reload.
    this.subscriptions.add(
      this.localeService.languageCode$.subscribe((code) => {
        this.locale = code === 'ar' ? 'ar' : 'en';
        this.dateRangeKey = null;
        this.buildRows();
        this.cdr.markForCheck();
      })
    );

    // Route params carry the sale; query params carry everything the reviewer
    // can change. Both are replayed on subscribe, so this fires immediately.
    this.subscriptions.add(
      combineLatest([this.route.paramMap, this.route.queryParamMap]).subscribe(
        ([params, query]) => {
          const house = params.get('house') ?? '';
          const sale = params.get('sale') ?? '';

          if (house !== this.houseSlug || sale !== this.saleSlug) {
            this.houseSlug = house;
            this.saleSlug = sale;
            this.loadSale();
          }

          this.applyQueryParams(query);
          this.loadLots();
        }
      )
    );

    this.subscriptions.add(
      this.lotNumberInput$
        .pipe(debounceTime(TEXT_FILTER_DEBOUNCE_MS))
        .subscribe((value) => {
          this.committedLotNumber = value;
          this.patchQuery({ lot: value || null, page: null });
        })
    );

    this.subscriptions.add(
      this.searchInput$
        .pipe(debounceTime(TEXT_FILTER_DEBOUNCE_MS))
        .subscribe((value) => {
          this.committedSearch = value;
          this.patchQuery({ q: value || null, page: null });
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.lotNumberInput$.complete();
    this.searchInput$.complete();
  }

  /* ---------------------------------------------------------------- *
   * Loading
   * ---------------------------------------------------------------- */

  /**
   * `getSale` answers `null` for an unknown slug pair — it never throws — so
   * the not-found state is a value, not an error path.
   */
  private loadSale(): void {
    const requestId = ++this.saleRequestId;
    this.saleLoading = true;
    this.sale = null;
    this.filterOptions = EMPTY_FILTER_OPTIONS;
    this.dateRangeKey = null;

    this.auctions.getSale(this.houseSlug, this.saleSlug).subscribe((sale) => {
      if (requestId !== this.saleRequestId) {
        return;
      }
      this.sale = sale;
      this.saleLoading = false;
      this.cdr.markForCheck();
    });

    // The dropdowns are built from the values that actually occur in this
    // catalogue, so they can never offer a filter that returns nothing.
    this.auctions
      .getLotFilterOptions(this.houseSlug, this.saleSlug)
      .subscribe((options) => {
        if (requestId !== this.saleRequestId) {
          return;
        }
        this.filterOptions = options;
        this.cdr.markForCheck();
      });
  }

  private loadLots(): void {
    const requestId = ++this.lotsRequestId;
    this.lotsLoading = true;

    this.auctions
      .listLots(this.houseSlug, this.saleSlug, this.buildLotQuery())
      .subscribe((result) => {
        if (requestId !== this.lotsRequestId) {
          return;
        }
        this.lots = result;
        // The service clamps an out-of-range `?page=` to the last real page;
        // follow it, so the pagination control agrees with what is on screen.
        this.page = result.page;
        this.buildRows();
        this.lotsLoading = false;
        this.cdr.markForCheck();
      });
  }

  private buildLotQuery(): LotQuery {
    const year = Number.parseInt(this.yearFoaled, 10);
    return {
      lot_number: this.lotNumberText || null,
      search: this.searchText || null,
      sex: this.sex || null,
      year_foaled: Number.isFinite(year) ? year : null,
      horse_type: this.horseType || null,
      consignor: this.consignor || null,
      sale_day: this.saleDay || null,
      page: this.page,
      per_page: DEFAULT_LOTS_PER_PAGE,
    };
  }

  /* ---------------------------------------------------------------- *
   * Query params — the single source of truth for every filter
   * ---------------------------------------------------------------- */

  private applyQueryParams(query: ParamMap): void {
    const view = (query.get('view') ?? '').toLowerCase();
    this.catalogueView = view === 'cards' ? 'cards' : 'table';

    const lot = query.get('lot') ?? '';
    // Only adopt the URL's value when it is not the one we just pushed, or a
    // round-trip through the router would reset the caret mid-word.
    if (lot !== this.committedLotNumber) {
      this.lotNumberText = lot;
      this.committedLotNumber = lot;
    }

    const search = query.get('q') ?? '';
    if (search !== this.committedSearch) {
      this.searchText = search;
      this.committedSearch = search;
    }

    this.sex = query.get('sex') ?? '';
    this.yearFoaled = query.get('year') ?? '';
    this.horseType = query.get('type') ?? '';
    this.consignor = query.get('consignor') ?? '';
    this.saleDay = query.get('day') ?? '';

    const page = Number.parseInt(query.get('page') ?? '1', 10);
    this.page = Number.isFinite(page) && page > 0 ? page : 1;

    if (this.activeFilterCount > 0) {
      // A shared link that already carries filters must show them, not hide
      // them behind a toggle the recipient has to find.
      this.filtersOpen = true;
    }
  }

  /** `null` removes a param; everything else merges over the current URL. */
  private patchQuery(patch: Params): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: patch,
      queryParamsHandling: 'merge',
    });
  }

  onLotNumberInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.lotNumberText = value;
    this.lotNumberInput$.next(value.trim());
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchText = value;
    this.searchInput$.next(value.trim());
  }

  /** Every dropdown. Selecting a value always returns to page 1. */
  onSelectFilter(param: 'sex' | 'year' | 'type' | 'consignor' | 'day', event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.patchQuery({ [param]: value || null, page: null });
  }

  onPageChanged(page: number): void {
    this.patchQuery({ page: page > 1 ? page : null });
  }

  setView(view: 'table' | 'cards'): void {
    if (view === this.catalogueView) {
      return;
    }
    this.patchQuery({ view });
  }

  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  clearFilters(): void {
    this.committedLotNumber = '';
    this.committedSearch = '';
    this.lotNumberText = '';
    this.searchText = '';
    this.patchQuery({
      lot: null,
      q: null,
      sex: null,
      year: null,
      type: null,
      consignor: null,
      day: null,
      page: null,
    });
  }

  get activeFilterCount(): number {
    return [
      this.lotNumberText,
      this.searchText,
      this.sex,
      this.yearFoaled,
      this.horseType,
      this.consignor,
      this.saleDay,
    ].filter((value) => value.trim().length > 0).length;
  }

  /* ---------------------------------------------------------------- *
   * Row view-models
   * ---------------------------------------------------------------- */

  private buildRows(): void {
    const items = this.lots?.items ?? [];
    this.rows = items.map((lot) => this.buildRow(lot));

    // ONE request for the whole page's stars, not one per card. The guest
    // catalogue request above is untouched and stays cacheable; this is a
    // second, private call that only happens for a signed-in buyer. It is a
    // no-op for guests and during SSR.
    if (items.length > 0) {
      this.myList.loadStates(items.map((lot) => lot.id)).subscribe();
    }
  }

  private buildRow(lot: ExternalLot): SaleCatalogueRow {
    const sire = (lot.sire ?? '').trim();
    const dam = (lot.dam ?? '').trim();

    return {
      id: lot.id,
      lotNumber: lot.lot_number,
      // Stored, not derived: an unnamed lot already carries the house's own
      // `Siyouni (FR) / Fabulous (IRE) B.F. (IRE)` shape in `display_title`.
      title: (lot.display_title ?? '').trim(),
      pedigree: sire && dam ? `${sire} ✕ ${dam}` : sire || dam,
      consignor: (lot.consignor ?? '').trim(),
      horseType: (lot.horse_type ?? '').trim(),
      // `null` here means the house published no price at all — a different
      // thing from zero, and a different thing again from "not sold".
      price: formatLotPrice(lot, this.locale),
      status: lot.status,
      statusKey: LOT_STATUS_KEYS[lot.status] ?? LOT_STATUS_KEYS.catalogued,
      hasOutcome: lot.status !== 'catalogued',
      isWithdrawn: lot.status === 'withdrawn',
      commands: [
        '/international-auctions',
        this.houseSlug,
        this.saleSlug,
        lot.lot_number,
      ],
    };
  }

  /* ---------------------------------------------------------------- *
   * Header / presentation
   * ---------------------------------------------------------------- */

  /**
   * The band's whole palette, memoised on the house record.
   *
   * A getter that allocated a fresh object every change-detection pass would
   * make the four `[style.--*]` bindings dirty every time, so the identity is
   * held until the house actually changes.
   */
  get brand(): HouseBrand {
    const house = this.sale?.house ?? null;

    if (house !== this.brandKey) {
      this.brandKey = house;
      this.brandValue = resolveHouseBrand(house);
    }

    return this.brandValue;
  }

  /** Kept: `formatLotPrice` and the status chip still read a single colour. */
  get brandColor(): string {
    return this.brand.color;
  }

  get brandContrast(): string {
    return this.brand.ink;
  }

  get saleName(): string {
    return resolveTranslation(this.sale?.name, this.locale) ?? '';
  }

  /**
   * The house's own venue when the sale carries none.
   *
   * An online sale has no ring to name, so `venue` is empty and the fact would
   * simply vanish from the header — leaving `24–25 August · 173 lots` with no
   * answer to "where". The house's `base_venue` is what a reader wants there:
   * "Online" for the timed sales, "Park Paddocks, Newmarket" for the ring.
   */
  get venueName(): string {
    return (
      resolveTranslation(this.sale?.venue, this.locale) ||
      resolveTranslation(this.sale?.house?.base_venue, this.locale) ||
      ''
    );
  }

  /**
   * `tattersalls.com` — what the outbound note promises the click will open.
   *
   * Derived from the house's `website_url` rather than stored a second time,
   * so it cannot drift from the site we actually link to. The `www.` is dropped
   * because the note is prose, not an address bar.
   */
  get houseDomain(): string {
    const url = (this.sale?.house?.website_url ?? '').trim();

    if (url === '') {
      return '';
    }

    // `URL` throws on anything unparseable; a house with a malformed website
    // should lose the note, not the header.
    try {
      return new URL(url).hostname.replace(/^www\./i, '');
    } catch {
      return '';
    }
  }

  /**
   * `24–25 August 2026 · Park Paddocks, Newmarket · 342 lots`.
   *
   * Built as a list so the template does not have to reason about which
   * separators survive when a fact is missing — an online sale with no venue
   * must not render two dots in a row.
   */
  get headerFacts(): SaleHeaderFact[] {
    const facts: SaleHeaderFact[] = [];

    if (this.dateRange) {
      facts.push({ key: 'dates', text: this.dateRange, isNumeric: true });
    }

    if (this.venueName) {
      facts.push({ key: 'venue', text: this.venueName, isNumeric: false });
    }

    if (this.declaredLotCountLabel) {
      facts.push({
        key: 'lots',
        // The house's own word: `lots` at Tattersalls, `hips` at Fasig-Tipton.
        text: `${this.declaredLotCountLabel} ${this.sale?.house?.lot_label_plural ?? ''}`.trim(),
        isNumeric: true,
      });
    }

    return facts;
  }

  /** What the house declares the catalogue holds, locale-formatted. */
  get declaredLotCountLabel(): string {
    const declared = this.sale?.declared_lot_count ?? 0;
    const entered = this.sale?.entered_lot_count ?? 0;
    const count = declared > 0 ? declared : entered;
    return count > 0 ? numberFormatter(this.intlLocale).format(count) : '';
  }

  get resultCountLabel(): string {
    const total = this.lots?.total ?? 0;
    return numberFormatter(this.intlLocale).format(total);
  }

  /** `Showing 25-48 of 1,580`, already locale-formatted. */
  get showingParams(): { from: string; to: string; total: string } {
    const format = numberFormatter(this.intlLocale);
    const total = this.lots?.total ?? 0;
    if (total === 0) {
      const zero = format.format(0);
      return { from: zero, to: zero, total: zero };
    }
    const perPage = this.lots?.per_page ?? DEFAULT_LOTS_PER_PAGE;
    const current = this.lots?.page ?? 1;
    const from = (current - 1) * perPage + 1;
    const to = Math.min(current * perPage, total);
    return {
      from: format.format(from),
      to: format.format(to),
      total: format.format(total),
    };
  }

  /**
   * `24–25 Aug 2026`. Memoised: the getter runs on every change-detection pass
   * and `Intl` range formatting is not free.
   */
  get dateRange(): string {
    if (!this.sale) {
      return '';
    }
    const key = `${this.locale}|${this.sale.starts_on}|${this.sale.ends_on}`;
    if (key !== this.dateRangeKey) {
      this.dateRangeKey = key;
      this.dateRangeValue = this.buildDateRange();
    }
    return this.dateRangeValue;
  }

  private buildDateRange(): string {
    const start = parseIsoDate(this.sale?.starts_on ?? '');
    const end = parseIsoDate(this.sale?.ends_on ?? '');
    const hasStart = Number.isFinite(start);
    const hasEnd = Number.isFinite(end);
    const formatter = dateFormatter(this.intlLocale);

    if (!hasStart && !hasEnd) {
      return '';
    }
    if (!hasStart) {
      return formatter.format(end);
    }
    if (!hasEnd || end <= start) {
      return formatter.format(start);
    }

    // `formatRange` collapses the shared fields and picks the separator per
    // locale. Guarded so an engine without it degrades rather than throws.
    if (typeof formatter.formatRange === 'function') {
      try {
        return formatter.formatRange(start, end);
      } catch {
        // Fall through.
      }
    }
    return `${formatter.format(start)} – ${formatter.format(end)}`;
  }

  private get intlLocale(): string {
    return INTL_LOCALES[this.locale] ?? INTL_LOCALES.en;
  }

  /* --- Links -------------------------------------------------------------
     Memoised arrays: a fresh literal per change-detection pass would re-run
     `RouterLink.ngOnChanges` on every tick.
     ---------------------------------------------------------------------- */

  get houseCommands(): unknown[] {
    if (this.houseSlug !== this.houseCommandsKey) {
      this.houseCommandsKey = this.houseSlug;
      this.houseCommandsValue = this.houseSlug
        ? ['/international-auctions', this.houseSlug]
        : ['/international-auctions'];
    }
    return this.houseCommandsValue;
  }

  readonly indexCommands: unknown[] = ['/international-auctions'];

  /**
   * `{environment.outboundUrl}/{token}` — NOT the auction house, and
   * deliberately a plain `href` rather than a `routerLink`.
   *
   * The server answers this path, writes the `external_events` row, appends UTM
   * and 302s to the sale's registration page (§8.3). That needs a real document
   * navigation, so the router must not be involved at all: it is a foreign
   * origin today, and a `routerLink` would swallow it whole. `| localeLink` is
   * skipped for the same reason — every other link on this page is locale-scoped
   * and does go through the pipe.
   *
   * No destination travels in the query string. It is held on the sale row, so
   * sending it would only offer a stranger somewhere to point our CTA. The one
   * thing that does travel is `lang`: the redirect lives on the API origin, so
   * the browser trims the `Referer` to a bare origin and the ledger has no other
   * way to know which language version produced the click (§8.4).
   */
  get registerHref(): string {
    const key = `${this.houseSlug}|${this.saleSlug}|${this.locale}`;
    if (key !== this.registerHrefKey) {
      this.registerHrefKey = key;
      const token = buildGoToken('registration', this.houseSlug, this.saleSlug);
      this.registerHrefValue =
        `${environment.outboundUrl}/${encodeURIComponent(token)}?lang=${this.locale}`;
    }
    return this.registerHrefValue;
  }

  /**
   * `registration_url` is read for its PRESENCE only; the value itself stays on
   * the server, which is the copy the redirect trusts.
   *
   * A sale with none is a sale we cannot tell anyone how to enter, and the
   * redirect 404s rather than inventing a destination — so the CTA is absent
   * instead of opening an error. Deliberately NOT falling back to the house's
   * homepage: that would count a registration click, the strongest intent
   * signal in §8.4, for a visitor we merely dropped on a front page.
   */
  get canRegister(): boolean {
    return (this.sale?.registration_url ?? '').trim() !== '';
  }
}

/*
 * A note on the empty and not-found states: they render their copy through the
 * `| translate` PIPE rather than through `app-no-data`, which takes already
 * resolved strings and would therefore need `TranslateService.instant()` at
 * `ngOnInit`. During SSR — and on a cold client before the i18n file has
 * settled — `instant()` hands back the raw key, which is exactly the text a
 * crawler would then cache for the not-found page. The pipe waits.
 */
